import styled from 'styled-components';
import { Checked } from 'ui/icons';
import type { ColumnData } from 'ui/tableHeader/constants';
import { body2, caption, colors, spacing } from 'ui/theme';
import { FeatureFlag, FractionalFlag, NonFractionFlag } from '../../types';
import { TableActionMenu } from './TableActionMenu';

const Cell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
  vertical-align: middle;
`;

const Notes = styled.div`
  margin-top: ${spacing.half};
  ${caption}
`;

const Affirmative = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  color: ${colors.green100};
`;

const StyledSpan = styled.span`
  &[data-enabled-globally='true'] {
    color: ${colors.textDisabled};
  }
`;

export const configureColumns = (
  handleEditClick: (featureFlag: FeatureFlag) => void,
  handleChangelogClick: (featureFlag: FeatureFlag) => void,
  handleImport: (featureFlag: FeatureFlag) => void,
) => [
  {
    name: 'Feature',
    value: 'feature',
    align: 'flex-start',
    RowCell: ({ row }: { row: FeatureFlag }) => (
      <Cell>
        <div>{row.feature}</div>
        {row.notes && <Notes>{row.notes}</Notes>}
      </Cell>
    ),
  } as ColumnData,
  {
    name: 'Enabled Globally',
    value: 'enabledGlobally',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => {
      if (row.isFractional) {
        return <Cell>{`${(row as FractionalFlag).settings.globalFraction * 100}%`}</Cell>;
      }

      return (
        <Cell>
          {(row as NonFractionFlag).settings.enabledGlobally ? (
            <Affirmative>
              <span>Yes</span>
              <Checked width={16} height={16} isColorInherited />
            </Affirmative>
          ) : (
            <span>No</span>
          )}
        </Cell>
      );
    },
  } as ColumnData,
  {
    name: 'Enabled Orgs',
    value: 'enabledOrgIds',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => {
      const isEnabledGlobally = (row as NonFractionFlag).settings.enabledGlobally;
      const displayValue = row.isFractional
        ? Object.keys((row as FractionalFlag).settings.orgIdFractions).length
        : (row as NonFractionFlag).settings.enabledOrgIds.length;

      return (
        <Cell>
          <StyledSpan data-enabled-globally={isEnabledGlobally}>
            {isEnabledGlobally ? '-' : displayValue}
          </StyledSpan>
        </Cell>
      );
    },
  } as ColumnData,
  {
    name: 'Enabled Projects',
    value: 'enabledProjectIds',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => {
      const isEnabledGlobally = (row as NonFractionFlag).settings.enabledGlobally;
      const displayValue = row.isFractional
        ? Object.keys((row as FractionalFlag).settings.projectIdFractions).length
        : (row as NonFractionFlag).settings.enabledProjectIds.length;

      return (
        <Cell>
          <StyledSpan data-enabled-globally={isEnabledGlobally}>
            {isEnabledGlobally ? '-' : displayValue}
          </StyledSpan>
        </Cell>
      );
    },
  } as ColumnData,
  {
    name: 'Disabled Orgs',
    value: 'disabledOrgIds',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => (
      <Cell>
        {row.isFractional ? '-' : (row as NonFractionFlag).settings.disabledOrgIds.length}
      </Cell>
    ),
  } as ColumnData,
  {
    name: 'Disabled Projects',
    value: 'disabledProjectIds',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => (
      <Cell>
        {row.isFractional ? '-' : (row as NonFractionFlag).settings.disabledProjectIds.length}
      </Cell>
    ),
  } as ColumnData,
  {
    name: 'Actions',
    value: '',
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }: { row: FeatureFlag }) => (
      <Cell>
        <TableActionMenu
          handleEditClick={() => handleEditClick(row)}
          handleChangelogClick={() => handleChangelogClick(row)}
          handleImport={() => handleImport(row)}
        />
      </Cell>
    ),
  } as ColumnData,
];
